import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaDownload, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Tabs } from 'antd';
import ApplicationService from '../services/axios.service';

const { TabPane } = Tabs;

const BulkUploadSalesTotal = () => {
  const [forecastFile, setForecastFile] = useState(null);
  const [budgetFile, setBudgetFile] = useState(null);
  const [uploadingForecast, setUploadingForecast] = useState(false);
  const [uploadingBudget, setUploadingBudget] = useState(false);
  const forecastFileInputRef = useRef(null);
  const budgetFileInputRef = useRef(null);
  const navigate = useNavigate();
  const service = new ApplicationService();

  const handleForecastFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        setForecastFile(selectedFile);
      } else {
        toast.warning('Please select a valid Excel file (.xlsx or .xls)');
        e.target.value = null;
      }
    }
  };

  const handleBudgetFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        setBudgetFile(selectedFile);
      } else {
        toast.warning('Please select a valid Excel file (.xlsx or .xls)');
        e.target.value = null;
      }
    }
  };

  const handleForecastUpload = async () => {
    if (!forecastFile) {
      toast.warning('Please select a forecast file to upload!');
      return;
    }

    setUploadingForecast(true);
    const formData = new FormData();
    formData.append('file', forecastFile);
    formData.append('type', 'forecast');

    try {
      const response = await service.uploadSalesTotal(formData);
      console.log('Forecast Response:', response);
      toast.success('Forecast uploaded successfully!');
      setForecastFile(null);
      if (forecastFileInputRef.current) {
        forecastFileInputRef.current.value = null;
      }
    } catch (error) {
      console.error('Error uploading forecast file:', error);
      toast.error('Error uploading forecast file!');
    } finally {
      setUploadingForecast(false);
    }
  };

  const handleBudgetUpload = async () => {
    if (!budgetFile) {
      toast.warning('Please select a budget file to upload!');
      return;
    }

    setUploadingBudget(true);
    const formData = new FormData();
    formData.append('file', budgetFile);

    try {
      const response = await service.uploadSalesBudget(formData);
      console.log('Budget Response:', response);
      toast.success('Budget uploaded successfully!');
      setBudgetFile(null);
      if (budgetFileInputRef.current) {
        budgetFileInputRef.current.value = null;
      }
    } catch (error) {
      console.error('Error uploading budget file:', error);
      toast.error('Error uploading budget file!');
    } finally {
      setUploadingBudget(false);
    }
  };

  const handleDownloadForecastTemplate = () => {
    const link = document.createElement('a');
    link.href = '/excel/sample_forecast_total.xlsx';
    link.download = 'forecast_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadBudgetTemplate = () => {
    const link = document.createElement('a');
    link.href = '/excel/SalesBudgetSample.xlsx';
    link.download = 'budget_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-[#1D1D1D] mb-6">Upload Sales Total</h2>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <Tabs defaultActiveKey="1">
          {/* Forecast Tab */}
          <TabPane tab="Forecast Upload" key="1">
            <div className="mb-6">
              <button
                onClick={handleDownloadForecastTemplate}
                className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer"
              >
                <FaDownload className="text-white" />
                <span>Download Forecast Template</span>
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
              <FaFileExcel className="text-6xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {forecastFile ? forecastFile.name : 'Drag and drop your Forecast Excel file here or click to browse'}
              </p>
              <input
                ref={forecastFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleForecastFileChange}
                className="hidden"
                id="forecast-file-upload"
              />
              <label
                htmlFor="forecast-file-upload"
                className="bg-[#287DD1] text-white px-6 py-3 rounded-md cursor-pointer inline-block"
              >
                Choose File
              </label>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleForecastUpload}
                disabled={!forecastFile || uploadingForecast}
                className={`flex-1 py-3 rounded-md flex items-center justify-center gap-x-2 ${
                  !forecastFile || uploadingForecast
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#287DD1] text-white cursor-pointer'
                }`}
              >
                <FaUpload />
                <span>{uploadingForecast ? 'Uploading...' : 'Upload Forecast'}</span>
              </button>
              <button
                onClick={() => navigate('/sales-total')}
                className="flex-1 bg-gray-500 text-white py-3 rounded-md"
              >
                Cancel
              </button>
            </div>
          </TabPane>

          {/* Budget Tab */}
          <TabPane tab="Budget Upload" key="2">
            <div className="mb-6">
              <button
                onClick={handleDownloadBudgetTemplate}
                className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer"
              >
                <FaDownload className="text-white" />
                <span>Download Budget Template</span>
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
              <FaFileExcel className="text-6xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                {budgetFile ? budgetFile.name : 'Drag and drop your Budget Excel file here or click to browse'}
              </p>
              <input
                ref={budgetFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleBudgetFileChange}
                className="hidden"
                id="budget-file-upload"
              />
              <label
                htmlFor="budget-file-upload"
                className="bg-[#287DD1] text-white px-6 py-3 rounded-md cursor-pointer inline-block"
              >
                Choose File
              </label>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleBudgetUpload}
                disabled={!budgetFile || uploadingBudget}
                className={`flex-1 py-3 rounded-md flex items-center justify-center gap-x-2 ${
                  !budgetFile || uploadingBudget
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#287DD1] text-white cursor-pointer'
                }`}
              >
                <FaUpload />
                <span>{uploadingBudget ? 'Uploading...' : 'Upload Budget'}</span>
              </button>
              <button
                onClick={() => navigate('/sales-total')}
                className="flex-1 bg-gray-500 text-white py-3 rounded-md"
              >
                Cancel
              </button>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default BulkUploadSalesTotal;


